import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { LaunchEventDetails } from 'types/event'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getScheduleDetailsForParticipant,
  setEventTaskStatus,
  startVirtualEvent,
} from 'modules/virtualClassrooms/student/ParticipantScheduleDetails/api/participantScheduleDetailsService'
import { ApiSetTaskStatusParams } from 'modules/virtualClassrooms/student/ParticipantScheduleDetails/types'
import {
  ParticipantScheduleDetailsState,
  ApiParticipantScheduleDetailsResponse,
  ReloadScheduleDetailsParams,
  ApiParticipantStartEventResponse,
} from './types'

const initialState: ParticipantScheduleDetailsState = {
  scheduleDetailsData: {},
  taskStatusUpdated: {},
  eventStarted: {},
  isPlayerOpen: false,
  eventParams: {
    eventUrl: '',
    exitUrl: '',
  },
}

const participantScheduleDetailsSlice = createSlice({
  name: 'virtualClassroomStudentParticipantScheduleDetails',
  initialState,
  reducers: {
    scheduleDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiParticipantScheduleDetailsResponse>>
    ) {
      state.scheduleDetailsData = action.payload
    },
    clearScheduleDetails(state) {
      state.scheduleDetailsData = initialState.scheduleDetailsData
      state.eventParams = initialState.eventParams
    },
    setTaskStatusProcessed(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.taskStatusUpdated = action.payload
    },
    startEventProcessed(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiParticipantStartEventResponse>>
    ) {
      state.eventStarted = action.payload
    },
    setPlayerVisibility(state, action: PayloadAction<boolean>) {
      state.isPlayerOpen = action.payload
    },
    setEventParams(state, action: PayloadAction<LaunchEventDetails>) {
      state.eventParams = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  scheduleDetailsLoaded,
  setTaskStatusProcessed,
  clearScheduleDetails,
  startEventProcessed,
  setPlayerVisibility,
  setEventParams,
} = participantScheduleDetailsSlice.actions

export { clearScheduleDetails, setPlayerVisibility }
export default participantScheduleDetailsSlice.reducer

export const loadParticipantScheduleDetailsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      scheduleDetails: () => getScheduleDetailsForParticipant(scheduleId),
    },
    payloadAction: scheduleDetailsLoaded,
    getResultToKeep: state =>
      state.virtualClassroomStudentParticipantScheduleDetails.scheduleDetailsData.result,
  })
}

const handleReloadScheduleDetails =
  (response: ApiMiddlewarePayload, params: ReloadScheduleDetailsParams): AppThunk =>
  async dispatch => {
    const { payloadAction, scheduleId } = params
    dispatch(payloadAction(response))
    if (!response.error)
      dispatch(loadParticipantScheduleDetailsData(scheduleId) as MiddlewareApiAction)
  }

export const setTaskStatus = (params: ApiSetTaskStatusParams): ApiAction => {
  const { scheduleId } = params
  return createApiAction({
    apiCalls: {
      status: () => setEventTaskStatus(params),
    },
    nextAction: {
      action: handleReloadScheduleDetails,
      params: { scheduleId, payloadAction: setTaskStatusProcessed },
    },
    payloadAction: setTaskStatusProcessed,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}

const handleStartEventRequest =
  (response: ApiMiddlewarePayload<ApiParticipantStartEventResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    dispatch(startEventProcessed(response))
    if (!error && result) {
      dispatch(setEventParams(result.status))
      dispatch(setPlayerVisibility(true))
    }
  }

export const startEvent = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      status: () => startVirtualEvent(scheduleId),
    },
    nextAction: handleStartEventRequest,
    payloadAction: startEventProcessed,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
  })
}
